import { FC, useMemo, useState } from "react";
import { Account, Asset, MsgExecute, MsgMulti, MsgSend } from "rujira.js";
import { coins } from "rujira.js/src/signers/cosmos";
import { Button, SwapSelect } from "rujira.ui";
import {
  BalanceCompact,
  usePreloadedBalance,
} from "../../common/components/Balance";
import {
  MsgProvider,
  TxBalanceAccountButton,
} from "../../common/components/TxButton";
import { useAccounts } from "../../services/accounts";
import { ChunkSlider } from "../../trade/components/InputQuantity";
import { AdjustmentSummary } from "./Common";
import { CdpSingle } from "./utils";

export const Repay: FC<{
  hideModal: () => void;
  p: CdpSingle<Asset>;
  registry: string;
}> = ({ hideModal, registry, p }) => {
  const { selected } = useAccounts();
  const [debtAmount, setDebtAmount] = useState<bigint>(0n);
  const [collateralAmount_, setCollateralAmount] = useState<bigint>(0n);
  const collateralAmount =
    collateralAmount_ === p.collateral.amount
      ? p.collateral.amount
      : collateralAmount_;
  const q = CdpSingle.repayAndWithdraw(p, debtAmount, collateralAmount);

  const debtBalance = usePreloadedBalance();
  const collateralMax = p.collateral.amount - q.collateral.minAdjust;
  const dust = collateralAmount === p.collateral.amount ? 10n : 0n;
  const amount = debtAmount + dust;

  const msg = useMemo(() => {
    if (!selected) return null;
    if (!debtAmount && !collateralAmount) return null;
    const debtDenom = p.debt.asset.variants?.native?.denom;
    const collateralDenom = p.collateral.asset.variants?.native?.denom;
    // Over-repay fractionally if we're withdrawing all collateral to ensure
    // that debt is fully repaid
    const acc: Account = { address: selected.address, network: "THOR" };
    return new MsgMulti(
      acc,
      [
        debtDenom && debtAmount
          ? new MsgSend(acc, p.debt.asset, amount, p.account.address)
          : null,
        new MsgExecute(acc, [], registry, {
          account: {
            addr: p.account.address,
            msgs: [
              debtDenom && debtAmount
                ? {
                    repay: {
                      denom: debtDenom,
                      amount: amount.toString(),
                    },
                  }
                : null,
              collateralAmount && collateralDenom
                ? {
                    send: {
                      to_address: selected.address,
                      funds: coins(
                        collateralAmount.toString(),
                        collateralDenom
                      ),
                    },
                  }
                : null,
            ].filter((x) => !!x),
          },
        }),
      ].filter((x) => !!x)
    );
  }, [selected, debtAmount, collateralAmount]);

  const max =
    debtBalance.balance?.balance && debtBalance.balance?.balance > p.debt.amount
      ? p.debt.amount
      : debtBalance.balance?.balance || 0n;

  return (
    <MsgProvider msg={msg}>
      <div style={{ marginLeft: "-0.5rem", marginRight: "-0.5rem" }}>
        <div className="row wrap gap-y-2 pad--sm mt-3">
          <div className="col-12 col-md-6">
            <h3 className="fs-14 fw-500 color-grey mb-1 ml-2">Repay Loan</h3>
            <BalanceCompact
              className="mb-0.5 px-1 ml-1"
              onClick={(v) => setDebtAmount(v.balance)}
            />

            <SwapSelect
              selected={p.debt.asset}
              amount={debtAmount}
              onChangeAmount={setDebtAmount}
              full
              className="bg-black"
            />

            <div className="flex ai-c mt-1.5 mx-2">
              <a
                className="fs-12 condensed fw-500 color-grey mr-0.5 pointer"
                onClick={() => setDebtAmount(0n)}>
                0%
              </a>
              <ChunkSlider
                amount={debtAmount}
                setAmount={setDebtAmount}
                max={max}
              />
              <a
                className="fs-12 condensed fw-500 color-grey ml-0.5 pointer"
                onClick={() => setDebtAmount(max)}>
                100%
              </a>
            </div>
          </div>
          <div className="col-12 col-md-6">
            <h3 className="fs-14 fw-500 color-grey mb-1 mb-md-3.5 ml-2">
              Withdraw Collateral
            </h3>
            <SwapSelect
              selected={p.collateral.asset}
              amount={collateralAmount}
              onChangeAmount={setCollateralAmount}
              full
              max={collateralMax}
              className="bg-black"
            />

            <div className="flex ai-c mt-1.5 mx-2">
              <a
                className="fs-12 condensed fw-500 color-grey mr-0.5 pointer"
                onClick={() => setCollateralAmount(0n)}>
                0%
              </a>
              <ChunkSlider
                amount={collateralAmount}
                setAmount={setCollateralAmount}
                max={collateralMax}
              />
              <a
                className="fs-12 condensed fw-500 color-grey ml-0.5 pointer"
                onClick={() => setCollateralAmount(collateralMax)}>
                100%
              </a>
            </div>
          </div>
          <AdjustmentSummary p={q} />
        </div>
      </div>
      <div className="modal__footer mt-4 px-3 py-2 flex ai-s jc-e wrap">
        <Button
          className="button--grey button--outline"
          onClick={hideModal}
          label="Cancel"
        />
        <div className="block ml-a text-right">
          <TxBalanceAccountButton
            required={{ amount, asset: debtBalance.asset }}
            label="Repay Loan"
            onSuccess={() => {
              if (p.debt.amount === 0n && p.collateral.amount === 0n)
                hideModal();
            }}
          />
        </div>
      </div>
    </MsgProvider>
  );
};
